import { RefreshIcon } from '@heroicons/react/solid'
import { useNavigate } from 'react-router-dom'
import { savedTokens } from 'src/auth'
import {
  Anchor,
  ErrorMissingData,
  ErrorNotLoggedIn,
  Paragraph,
  Title,
} from 'src/components'
import {
  savedAccountId,
  savedArn,
  savedConnectUrl,
  savedInstanceId,
  savedRegion,
} from '../storage'
import CustomButton from 'src/components/Shared/UIComponents/Button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'src/ui/tabs'
import { AzureADContent } from './SSOContents/AzureADContent'
import { AWSIAMContent } from './SSOContents/AWSIAMContent'
import { GoogleWorkspaceContent } from './SSOContents/GoogleWorkspaceContent'
import { OktaContent } from './SSOContents/OktaContent'

type Props = {
  organization: string
  username: string
  connectUrl: string
  instanceId: string
  accountId: string
  region: string
  arn: string
}

function Sso(props: Props) {
  return (
    <>
      <Title label="SSO" className="pb-6" />
      <Paragraph className="col-span-full mb-5">
        The following Identity Providers are available via SSO: Azure Active
        Directory, AWS IAM identity center, Google Workspace and Okta.
      </Paragraph>
      <Paragraph className="col-span-full">
        Choose the identity provider of your choice below and follow the
        provided instructions. In case you would like to integrate with a
        different identity provider, please contact us via{' '}
        <Anchor href="mailto:mc.notifications@idealsystems.be">
          mc.notifications@idealsystems.be
        </Anchor>
        .
      </Paragraph>

      <div className="col-span-full grid gap-2 mt-5">
        <Tabs defaultValue="azure" className="w-full">
          <TabsList className="flex max-w-5xl m-auto items-start">
            <TabsTrigger value="azure">Azure AD</TabsTrigger>
            <TabsTrigger value="awsiam">AWS IAM</TabsTrigger>
            <TabsTrigger value="googleworkspace">Google Workspace</TabsTrigger>
            <TabsTrigger value="okta">Okta</TabsTrigger>
          </TabsList>
          <TabsContent value="azure" className="flex flex-col bg-white">
            <AzureADContent {...props} />
          </TabsContent>
          <TabsContent value="awsiam" className="flex flex-col bg-white">
            <AWSIAMContent {...props} />
          </TabsContent>
          <TabsContent
            value="googleworkspace"
            className="flex flex-col bg-white"
          >
            <GoogleWorkspaceContent {...props} />
          </TabsContent>
          <TabsContent value="okta" className="flex flex-col bg-white">
            <OktaContent {...props} />
          </TabsContent>
        </Tabs>
      </div>
    </>
  )
}

export default function ValidateSso() {
  const navigate = useNavigate()
  const [tokens] = savedTokens.useState(null)
  const connectUrl = savedConnectUrl.get()
  const instanceId = savedInstanceId.get()
  const accountId = savedAccountId.get()
  const arn = savedArn.get()
  const region = savedRegion.get()

  if (!tokens) {
    return <ErrorNotLoggedIn />
  }

  if (!connectUrl || !instanceId || !accountId || !region || !arn) {
    return (
      <ErrorMissingData
        missingValues={{
          connectUrl: !connectUrl,
          instanceId: !instanceId,
          accountId: !accountId,
          arn: !arn,
        }}
      >
        <CustomButton
          label="restart setup"
          onClick={() => navigate('../arn')}
          className="ml-8"
          icon={<RefreshIcon />}
          iconPosition="left"
        />
      </ErrorMissingData>
    )
  }

  return (
    <Sso
      organization={tokens.organization}
      username={tokens.username}
      instanceId={instanceId}
      accountId={accountId}
      connectUrl={connectUrl}
      region={region}
      arn={arn}
    />
  )
}
